import * as React from "react";
import { cn } from "@/lib/utils";

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "ghost" | "link";
  size?: "sm" | "md" | "lg";
};

const variantClass: Record<NonNullable<ButtonProps["variant"]>, string> = {
  primary:
    "bg-accent-600 text-white hover:bg-accent-700 " +
    "active:bg-accent-700 disabled:bg-[var(--color-text-subtle)] " +
    "disabled:cursor-not-allowed",
  secondary:
    "bg-brand-500 text-white hover:bg-brand-700 " +
    "active:bg-brand-900 disabled:bg-[var(--color-text-subtle)]",
  ghost:
    "bg-transparent text-text border border-border-strong " +
    "hover:bg-bg-muted disabled:opacity-50",
  link: "bg-transparent text-brand-500 underline-offset-2 hover:underline p-0",
};

const sizeClass: Record<NonNullable<ButtonProps["size"]>, string> = {
  sm: "h-9 px-3 text-sm rounded-[8px]",
  md: "h-11 px-5 text-base rounded-[8px]",
  lg: "h-14 px-7 text-lg rounded-[8px]",
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "primary", size = "md", ...props }, ref) => (
    <button
      ref={ref}
      className={cn(
        "inline-flex items-center justify-center font-medium transition-colors",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500",
        variantClass[variant],
        variant !== "link" && sizeClass[size],
        className,
      )}
      {...props}
    />
  ),
);
Button.displayName = "Button";
